"use client"

import { useRef } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

type HomePromotionsProps = {
  locale?: string
}

export default function HomePromotions({
  locale = "en",
}: HomePromotionsProps) {
  const desktopScrollRef = useRef<HTMLDivElement>(null)
  const mobileScrollRef = useRef<HTMLDivElement>(null)

  const lang =
    locale?.startsWith("he") || locale?.startsWith("il")
      ? "he"
      : locale?.startsWith("ru")
      ? "ru"
      : "en"

  const banners = [
    {
      title: "Weekly Sale",
      image: `/images/banners/weekly-sale-${lang}.webp`,
      href: "/store",
    },
    {
      title: "New Arrivals",
      image: `/images/banners/new-arrivals-${lang}.webp`,
      href: "/store",
    },
    {
      title: "Bestsellers",
      image: `/images/banners/home-slider-04-bestsellers-${lang}.webp`,
      href: "/store",
    },
    {
      title: "Clearance",
      image: `/images/banners/home-slider-03-clearance-${lang}.webp`,
      href: "/store",
    },
  ]

  const scrollByCard = (direction: "left" | "right") => {
    const container = desktopScrollRef.current
    if (!container) return

    const firstCard = container.querySelector("[data-banner-card]") as HTMLElement | null
    if (!firstCard) return

    const cardWidth = firstCard.offsetWidth
    const gap = 16
    const amount = cardWidth + gap

    container.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    })
  }

  return (
    <section className="w-full py-6 small:py-8">
      <div className="content-container">
        <div className="hidden medium:grid grid-cols-[56px_minmax(0,1fr)_56px] items-center gap-3">
          <button
            type="button"
            onClick={() => scrollByCard("left")}
            aria-label="Scroll banners left"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-gray-300 bg-white text-orange-500 shadow-md hover:bg-orange-50"
          >
            <span className="text-2xl leading-none">‹</span>
          </button>

          <div
            ref={desktopScrollRef}
            className="
              flex gap-4 overflow-x-auto scroll-smooth pb-2
              snap-x snap-mandatory
              [scrollbar-width:none] [-ms-overflow-style:none]
              [&::-webkit-scrollbar]:hidden
            "
          >
            {banners.map((banner) => (
              <LocalizedClientLink
                key={banner.title}
                href={banner.href}
                data-banner-card
                className="snap-start shrink-0 block w-[31.2%]"
              >
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:shadow-md">
                  <img
                    src={banner.image}
                    alt={banner.title}
                    className="block w-full h-auto"
                    loading="lazy"
                  />
                </div>
              </LocalizedClientLink>
            ))}
          </div>

          <button
            type="button"
            onClick={() => scrollByCard("right")}
            aria-label="Scroll banners right"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-gray-300 bg-white text-orange-500 shadow-md hover:bg-orange-50"
          >
            <span className="text-2xl leading-none">›</span>
          </button>
        </div>

        <div
          ref={mobileScrollRef}
          className="
            flex gap-4 overflow-x-auto scroll-smooth pb-2
            snap-x snap-mandatory
            [scrollbar-width:none] [-ms-overflow-style:none]
            [&::-webkit-scrollbar]:hidden
            medium:hidden
          "
        >
          {banners.map((banner) => (
            <LocalizedClientLink
              key={banner.title}
              href={banner.href}
              className="snap-start shrink-0 block w-[82%] small:w-[48%]"
            >
              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:shadow-md">
                <img
                  src={banner.image}
                  alt={banner.title}
                  className="block w-full h-auto"
                  loading="lazy"
                />
              </div>
            </LocalizedClientLink>
          ))}
        </div>
      </div>
    </section>
  )
}